import React, { useState, useMemo, useCallback, useRef } from 'react';
import {
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    IconButton, Tooltip, TablePagination, TextField,
    CircularProgress, Autocomplete, InputAdornment,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useTranslation } from 'react-i18next';
import { beautifyNumber } from '../../../Utils/FormatNumber';
import ClientService from '../../../Services/client.service';
import EnumsService from '../../../Services/enums.service';
import CustomYearDatePicker from '../../Widgets/CustomDates/CustomYearDatePicker/CustomYearDatePicker';
import { formatApiDateForDisplay, parseApiDateToUtcIso } from '../../../Utils/dateParseUtils';
import AssetModalShell from './AssetModalShell/AssetModalShell';
import shell from './AssetModalShell/AssetModalShell.module.scss';
import { xtramInputSx, xtramPopperSx } from './AssetModalShell/assetModalInputs';


/** SortCodeDetails returns { dateRange: { segment: { sortCodeId: { name: [...] } } } } */
const extractSortCodeTransactions = (apiData) => {
    if (!apiData) return [];
    if (Array.isArray(apiData)) return apiData;
    if (Array.isArray(apiData.values)) return apiData.values;
    if (Array.isArray(apiData.data)) return apiData.data;
    if (typeof apiData !== 'object') return [];

    try {
        const firstDict = apiData[Object.keys(apiData)[0]];
        const secondDict = firstDict?.[Object.keys(firstDict || {})[0]];
        const thirdDict = secondDict?.[Object.keys(secondDict || {})[0]];
        const values = thirdDict?.[Object.keys(thirdDict || {})[0]];
        return Array.isArray(values) ? values : [];
    } catch (_) {
        return [];
    }
};

const getTxTypeLabel = (tx, t) => {
    const raw = tx['t-type'] || tx.docType || tx.type;
    if (raw === 'Incomes' || raw === 3 || raw === 'Income') return t('Income') || 'הכנסה';
    if (raw === 'Expenses' || raw === 1) return t('Expense') || 'הוצאה';
    return t('JournalEntry') || 'פקודת יומן';
};

const getTxSelectType = (tx) => {
    const raw = tx['t-type'] || tx.docType || tx.type;
    if (raw === 'Incomes' || raw === 3 || raw === 'Income') return 'Incomes';
    if (raw === 'Expenses' || raw === 1) return 'Expenses';
    return 'JournalEntries';
};

/** Prefer net-of-VAT (t-amount-vat) for asset sell amount; fall back to gross fields. */
const getTxAmount = (tx) => {
    const vatAmount = tx['t-amount-vat'];
    const raw = (vatAmount != null && vatAmount !== '')
        ? vatAmount
        : (tx.amount || tx.Amount || tx['t-amount-no-vat'] || 0);
    const n = Number(raw);
    if (isNaN(n)) return 0;
    return Math.round(n * 100) / 100;
};

const SaleTransactionPicker = ({
    open,
    onClose,
    onSelect,
    caseId,
    accountantId,
    token,
    sortCodeOptions = [],
}) => {
    const { t, i18n } = useTranslation();
    const direction = i18n.dir();

    const [selectedSortCode, setSelectedSortCode] = useState(null);
    const [transactions, setTransactions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [fetched, setFetched] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const initialYear = useMemo(() => new Date(), []);
    const yearRef = useRef(initialYear);
    const [year, setYear] = useState(initialYear);

    const handleYearChange = useCallback((d) => {
        yearRef.current = d;
        setYear(d);
    }, []);

    const getYearDateRange = useCallback((dateVal) => {
        const y = dateVal
            ? new Date(dateVal).getUTCFullYear()
            : new Date().getUTCFullYear();
        return {
            startDate: new Date(Date.UTC(y, 0, 1)),
            endDate: new Date(Date.UTC(y, 11, 31, 23, 59, 59, 999)),
        };
    }, []);

    const defaultSortCode = useMemo(() => {
        return sortCodeOptions.find(sc =>
            sc.sortCodeName?.includes('מכירת ציוד') || sc.sortCodeName?.includes('מכירת צי')
        ) || null;
    }, [sortCodeOptions]);

    React.useEffect(() => {
        if (open && defaultSortCode && !selectedSortCode) {
            setSelectedSortCode(defaultSortCode);
        }
    }, [open, defaultSortCode]);

    const handleFetch = useCallback(async () => {
        const scId = selectedSortCode?.sortCodeId;
        if (!scId || !caseId || !accountantId) return;

        setLoading(true);
        setFetched(false);
        try {
            const { startDate, endDate } = getYearDateRange(yearRef.current ?? year);

            const res = await ClientService.SortCodeDetails(
                caseId, accountantId, startDate, endDate, token, scId, false
            );
            if (!res?.ok) throw new Error('Failed to fetch');
            const data = await res.json();
            const values = extractSortCodeTransactions(data);
            setTransactions(values);
            setFetched(true);
            setPage(0);
        } catch (err) {
            console.error('SaleTransactionPicker fetch error:', err);
            setTransactions([]);
            setFetched(true);
        } finally {
            setLoading(false);
        }
    }, [selectedSortCode, caseId, accountantId, token, year, getYearDateRange]);

    const filtered = useMemo(() => {
        const list = Array.isArray(transactions) ? transactions : [];
        if (!searchTerm.trim()) return list;
        const q = searchTerm.trim().toLowerCase();
        return list.filter(tx => {
            const desc = (tx.description || tx.Description || tx['t-details'] || '').replace(/^null$/i, '');
            const ref = tx.reference || tx.Reference || tx['t-reference'] || '';
            const amount = getTxAmount(tx);
            const docDate = tx.documentDate || tx.DocumentDate || tx['t-document-date'] || '';
            return (
                desc.toLowerCase().includes(q) ||
                ref.toLowerCase().includes(q) ||
                String(amount).includes(q) ||
                docDate.toLowerCase().includes(q)
            );
        });
    }, [transactions, searchTerm]);

    const paginated = useMemo(() => {
        return filtered.slice(page * rowsPerPage, (page + 1) * rowsPerPage);
    }, [filtered, page, rowsPerPage]);

    const handleSelect = (tx) => {
        const id = tx.id || tx.Id;
        const type = getTxSelectType(tx);
        const amount = getTxAmount(tx);
        const date = parseApiDateToUtcIso(
            tx.documentDate || tx.DocumentDate || tx['t-document-date']
        );
        onSelect({ id, type, amount, date });
        handleClose();
    };

    const handleClose = () => {
        setTransactions([]);
        setFetched(false);
        setSearchTerm('');
        setPage(0);
        onClose();
    };

    if (!open) return null;

    return (
        <AssetModalShell
            open={open}
            onClose={handleClose}
            title={t('SelectSaleTransaction') || 'בחר תנועת מכירה'}
            direction={direction}
            size="lg"
            zIndex={100000}
            toolbar={(
                <div className={shell.toolbar} style={{ alignItems: 'flex-end' }}>
                    <div className={shell.field} style={{ flex: 1, minWidth: 220 }}>
                        <span className={shell.fieldLabel}>{t('SortCode') || 'מיון'}</span>
                        <Autocomplete
                            size="small"
                            options={sortCodeOptions}
                            getOptionLabel={(opt) => `${opt.sortCodeId} - ${opt.sortCodeName}`}
                            value={selectedSortCode}
                            onChange={(_, v) => setSelectedSortCode(v)}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    placeholder={t('SelectSortCode') || 'בחר מיון'}
                                    variant="standard"
                                    InputProps={{ ...params.InputProps, disableUnderline: true, sx: xtramInputSx() }}
                                />
                            )}
                            slotProps={{ popper: { style: { zIndex: 200000 }, sx: xtramPopperSx } }}
                        />
                    </div>
                    <div className={shell.field} style={{ width: 130 }}>
                        <span className={shell.fieldLabel}>{t('Year') || 'שנה'}</span>
                        <CustomYearDatePicker
                            date={year}
                            onDateChange={handleYearChange}
                            height="var(--xtram-control-height, 40px)"
                        />
                    </div>
                    <button
                        type="button"
                        className={shell.blueBtn}
                        onClick={handleFetch}
                        disabled={!selectedSortCode || loading}
                    >
                        {loading ? <CircularProgress size={18} color="inherit" /> : (t('FetchTransactions') || 'טען תנועות')}
                    </button>
                </div>
            )}
            footer={(
                <button type="button" className={shell.secondaryBtn} onClick={handleClose}>
                    {t('Close') || 'סגור'}
                </button>
            )}
        >
            {!fetched ? (
                <div className={shell.emptyState}>
                    {t('SelectSortCode') || 'בחר מיון'} · {t('FetchTransactions') || 'טען תנועות'}
                </div>
            ) : (
                <div className={shell.stack}>
                    <div className={shell.toolbar}>
                        <span className={shell.stat}>
                            {t('TransactionsFound') || 'תנועות נמצאו'}: <strong>{filtered.length}</strong>
                        </span>
                        <TextField
                            placeholder={t('Search') || 'חיפוש'}
                            value={searchTerm}
                            onChange={(e) => { setSearchTerm(e.target.value); setPage(0); }}
                            variant="standard"
                            className={`${shell.searchInput} ${shell.toolbarEnd}`}
                            InputProps={{
                                disableUnderline: true,
                                startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>,
                            }}
                        />
                    </div>

                    <div className={shell.tableWrap}>
                        <TableContainer sx={{ maxHeight: 360 }}>
                            <Table size="small" stickyHeader dir={direction}>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>{t('DocumentDate') || 'תאריך'}</TableCell>
                                        <TableCell>{t('Description') || 'תיאור'}</TableCell>
                                        <TableCell>{t('Reference') || 'אסמכתא'}</TableCell>
                                        <TableCell>{t('Amount') || 'סכום'}</TableCell>
                                        <TableCell>{t('Type') || 'סוג'}</TableCell>
                                        <TableCell align="center" sx={{ width: 64 }}>{t('Select') || 'בחר'}</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {paginated.map((tx, idx) => {
                                        const docDate = tx.documentDate || tx.DocumentDate || tx['t-document-date'];
                                        const desc = (tx.description || tx.Description || tx['t-details'] || '').replace(/^null$/i, '');
                                        const ref = tx.reference || tx.Reference || tx['t-reference'] || '';
                                        const amount = getTxAmount(tx);
                                        const typeLabel = getTxTypeLabel(tx, t);
                                        return (
                                            <TableRow key={tx.id || tx.Id || idx} hover className={shell.rowClickable} onClick={() => handleSelect(tx)}>
                                                <TableCell>{formatApiDateForDisplay(docDate)}</TableCell>
                                                <TableCell sx={{ fontWeight: 600 }}>{desc}</TableCell>
                                                <TableCell>{ref}</TableCell>
                                                <TableCell sx={{ fontWeight: 700 }}>{beautifyNumber(amount, 2)}</TableCell>
                                                <TableCell>{typeLabel}</TableCell>
                                                <TableCell align="center">
                                                    <Tooltip title={t('SelectThisTransaction') || 'בחר תנועה זו'}>
                                                        <IconButton size="small" sx={{ color: '#1d6f42' }}>
                                                            <CheckCircleIcon sx={{ fontSize: 18 }} />
                                                        </IconButton>
                                                    </Tooltip>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                    {paginated.length === 0 && (
                                        <TableRow>
                                            <TableCell colSpan={6}>
                                                <div className={shell.emptyState}>{t('NoTransactionsFound') || 'לא נמצאו תנועות'}</div>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <TablePagination
                            component="div"
                            className={shell.pagination}
                            rowsPerPageOptions={[5, 10, 25]}
                            count={filtered.length}
                            rowsPerPage={rowsPerPage}
                            page={page}
                            onPageChange={(_, p) => setPage(p)}
                            onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                            labelRowsPerPage={t('RowsPerPage') || 'שורות'}
                        />
                    </div>
                </div>
            )}
        </AssetModalShell>
    );
};

export default SaleTransactionPicker;
